import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Step,
  StepButton,
  Stepper,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { BrandConfig, CloneCreate, TenantAnswers } from '@api/api';
import { endpoints, qk } from '../api/endpoints';
import { errorMessage } from '../api/client';
import { ErrorState, PageHeader, KeyValue, SkeletonRows } from '../components/common';
import { envChanges } from './clone/KeysTab';
import { BrandForm, EnvForm, TenantForm } from '../components/forms';
import { LoadingButton } from '../components/LoadingButton';
import { EMPTY_BRAND, EMPTY_TENANT, REQUIRED_GROUPS, GROUP_ORDER, brandErrors, missingRequired, tenantErrors, type Errors } from '../lib/validate';
import { domainError, slugError, slugify } from '../lib/slug';
import { FEE_CURRENCIES } from './clone/SettingsTab';

const STEPS = ['Client', 'Tenant', 'Keys', 'Brand', 'Review'];

export interface ClientForm {
  name: string;
  clientName: string;
  clientContact: string;
  slug: string;
  domain: string;
  releaseId: number | '';
  licence: '' | 'rent' | 'sale';
  monthlyFee: string;
  feeCurrency: string;
  renewalDate: string;
  notes: string;
}

export function clientErrors(f: ClientForm, taken: string[]): Errors {
  const e: Errors = {};
  if (!f.name.trim()) e.name = 'Required';
  if (!f.clientName.trim()) e.clientName = 'Required';
  const s = slugError(f.slug, taken);
  if (s) e.slug = s;
  const d = domainError(f.domain);
  if (d) e.domain = d;
  if (f.monthlyFee !== '' && (Number.isNaN(Number(f.monthlyFee)) || Number(f.monthlyFee) < 0)) e.monthlyFee = 'Not a number';
  return e;
}

type SubmitStep = 'keys' | 'brand' | 'check' | 'provision';
const SUBMIT_LABEL: Record<SubmitStep, string> = { keys: 'Save keys', brand: 'Save brand', check: 'Check the draft', provision: 'Start provisioning' };

export function NewCloneWizard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('sm'));
  const [step, setStep] = useState(0);
  const [showErrors, setShowErrors] = useState<Record<number, boolean>>({});

  const settings = useQuery({ queryKey: qk.settings, queryFn: endpoints.settings, retry: false });
  const releases = useQuery({ queryKey: qk.releases, queryFn: endpoints.releases });
  const clones = useQuery({ queryKey: qk.clones, queryFn: endpoints.clones });
  const baseDomain = settings.data?.baseDomain ?? 'x123.digital';
  const taken = useMemo(() => (clones.data ?? []).map((c) => c.slug), [clones.data]);

  const [client, setClient] = useState<ClientForm>({ name: '', clientName: '', clientContact: '', slug: '', domain: '', releaseId: '', licence: 'rent', monthlyFee: '', feeCurrency: 'EUR', renewalDate: '', notes: '' });
  const [slugTouched, setSlugTouched] = useState(false);
  const [domainTouched, setDomainTouched] = useState(false);
  const [tenant, setTenant] = useState<TenantAnswers>(EMPTY_TENANT);
  const [tenantTouched, setTenantTouched] = useState(false);
  const [env, setEnv] = useState<Record<string, string | null>>({});
  const [withBrand, setWithBrand] = useState(false);
  const [brand, setBrand] = useState<BrandConfig>(EMPTY_BRAND);

  // defaults that follow the client step until the user edits them
  useEffect(() => {
    if (client.releaseId === '' && settings.data?.defaultReleaseId) setClient((c) => ({ ...c, releaseId: settings.data!.defaultReleaseId! }));
  }, [settings.data, client.releaseId]);
  const setC = (p: Partial<ClientForm>) =>
    setClient((c) => {
      const n = { ...c, ...p };
      if (p.name !== undefined && !slugTouched) n.slug = slugify(p.name);
      if ((p.name !== undefined || p.slug !== undefined) && !domainTouched) n.domain = n.slug ? `${n.slug}.${baseDomain}` : '';
      return n;
    });
  useEffect(() => {
    if (!tenantTouched) setTenant((t) => ({ ...t, brandName: client.name, adminLogin: client.domain ? `admin@${client.domain}` : '' }));
  }, [client.name, client.domain, tenantTouched]);
  useEffect(() => {
    if (!withBrand) {
      const rev = client.domain.split('.').reverse().join('.');
      setBrand((b) => ({ ...b, appName: client.name, riderBundleId: rev, staffBundleId: rev ? `${rev}.staff` : '', androidPackage: rev.replace(/-/g, '_') }));
    }
  }, [client.name, client.domain, withBrand]);

  // The draft is created when leaving the tenant step, so the keys step works on the server's own
  // field list (with tenant fallbacks and generated secrets) and nothing typed is lost.
  const [cloneId, setCloneId] = useState<number | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const draft = useQuery({ queryKey: qk.clone(cloneId ?? 0), queryFn: () => endpoints.clone(cloneId!), enabled: !!cloneId });
  const envQ = useQuery({ queryKey: qk.env(cloneId ?? 0), queryFn: () => endpoints.env(cloneId!), enabled: !!cloneId });

  const cErr = clientErrors(client, cloneId ? taken.filter((t) => t !== client.slug) : taken);
  const tErr = tenantErrors(tenant);
  const fields = envQ.data ?? [];
  const missing = cloneId && envQ.data ? missingRequired(fields, env, draft.data?.missingRequired) : [];
  const bErr = withBrand ? brandErrors(brand) : {};
  const stepValid = [Object.keys(cErr).length === 0, Object.keys(tErr).length === 0, true, Object.keys(bErr).length === 0, true];

  const clientBody = (): CloneCreate => ({
    slug: client.slug,
    name: client.name.trim(),
    clientName: client.clientName.trim(),
    clientContact: client.clientContact.trim() || undefined,
    domain: client.domain,
    releaseId: client.releaseId || undefined,
    licence: client.licence || undefined,
    monthlyFee: client.monthlyFee === '' ? undefined : Number(client.monthlyFee),
    feeCurrency: client.feeCurrency || undefined,
    renewalDate: client.renewalDate || undefined,
    notes: client.notes.trim() || undefined,
  });

  /** Create the draft (or update it) and save the tenant answers. */
  const saveDraft = async (): Promise<number | null> => {
    setSavingDraft(true);
    setDraftError(null);
    try {
      let id = cloneId;
      if (!id) {
        id = (await endpoints.createClone(clientBody())).id;
        setCloneId(id);
      } else {
        const { slug: _slug, ...patch } = clientBody();
        void _slug;
        await endpoints.patchClone(id, patch);
      }
      await endpoints.putTenant(id, tenant);
      void qc.invalidateQueries({ queryKey: ['clone', id] });
      void qc.invalidateQueries({ queryKey: ['clones'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
      return id;
    } catch (e) {
      setDraftError(errorMessage(e));
      return null;
    } finally {
      setSavingDraft(false);
    }
  };

  const next = async () => {
    if (!stepValid[step]) {
      setShowErrors((s) => ({ ...s, [step]: true }));
      return;
    }
    if (step === 1 && !(await saveDraft())) return;
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo?.({ top: 0 });
  };

  // ---------------------------------------------------------------- submit
  const [progress, setProgress] = useState<Partial<Record<SubmitStep, 'running' | 'done' | 'failed'>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (provision: boolean) => {
    if (!cloneId) return;
    const id = cloneId;
    setSubmitting(true);
    setSubmitError(null);
    setProgress({});
    const mark = (s: SubmitStep, v: 'running' | 'done' | 'failed') => setProgress((p) => ({ ...p, [s]: v }));
    let current: SubmitStep = 'keys';
    try {
      const values = envChanges(fields, env);
      if (Object.keys(values).length) {
        mark('keys', 'running');
        await endpoints.putEnv(id, values);
        mark('keys', 'done');
        setEnv({});
        void qc.invalidateQueries({ queryKey: qk.env(id) });
      }
      if (withBrand) {
        current = 'brand';
        mark('brand', 'running');
        await endpoints.putBrand(id, brand);
        mark('brand', 'done');
      }
      void qc.invalidateQueries({ queryKey: ['clones'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
      if (!provision) {
        navigate(`/clones/${id}`);
        return;
      }
      current = 'check';
      mark('check', 'running');
      const d = await endpoints.clone(id);
      qc.setQueryData(qk.clone(id), d);
      if (d.missingRequired.length) throw new Error(`Required keys missing: ${d.missingRequired.join(', ')}`);
      if (!d.tenantComplete) throw new Error('The tenant answers are incomplete');
      mark('check', 'done');
      current = 'provision';
      mark('provision', 'running');
      const job = await endpoints.action(id, 'provision');
      mark('provision', 'done');
      navigate(`/jobs/${job.id}?fresh=1`);
    } catch (e) {
      mark(current, 'failed');
      setSubmitError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const err = (k: keyof ClientForm) => ((showErrors[0] || (k === 'slug' && client.slug) || (k === 'domain' && domainTouched)) && cErr[k] ? { error: true, helperText: cErr[k] } : {});
  const release = releases.data?.find((r) => r.id === client.releaseId);

  return (
    <>
      <PageHeader title="New clone" subtitle="A complete installation for a client: stack, CRM, domain, TLS, tenant and keys." />
      <Stepper activeStep={step} nonLinear alternativeLabel={!wide} sx={{ mb: 3 }}>
        {STEPS.map((label, i) => (
          <Step key={label} completed={i < step && stepValid[i]}>
            <StepButton onClick={() => (i < step || stepValid.slice(0, i).every(Boolean) ? setStep(i) : undefined)} optional={i === 3 ? <Typography variant="caption">optional</Typography> : undefined}>
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ maxWidth: 980 }}>
        {step === 0 && (
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Clone name" placeholder="VoltRide Kyiv" value={client.name} onChange={(e) => setC({ name: e.target.value })} {...err('name')} autoFocus />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Client (company)" value={client.clientName} onChange={(e) => setC({ clientName: e.target.value })} {...err('clientName')} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Contact" placeholder="Name, phone, e-mail" value={client.clientContact} onChange={(e) => setC({ clientContact: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    label="Slug"
                    value={client.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setC({ slug: e.target.value.toLowerCase() });
                    }}
                    disabled={!!cloneId}
                    inputProps={{ 'aria-label': 'Slug', spellCheck: false }}
                    {...(err('slug').error ? err('slug') : { helperText: cloneId ? 'The draft exists; the slug is fixed.' : 'Folder, compose project and subdomain. Cannot change later.' })}
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    fullWidth
                    label="Domain"
                    value={client.domain}
                    onChange={(e) => {
                      setDomainTouched(true);
                      setC({ domain: e.target.value.trim().toLowerCase() });
                    }}
                    {...(err('domain').error ? err('domain') : { helperText: client.domain.endsWith(`.${baseDomain}`) ? 'Wildcard DNS covers it.' : `A custom domain must have an A record to ${settings.data?.serverIp ?? 'the server IP'}.` })}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    select
                    fullWidth
                    label="Release"
                    value={client.releaseId}
                    onChange={(e) => setC({ releaseId: Number(e.target.value) })}
                    helperText={releases.data && !releases.data.some((r) => r.status === 'ready') ? 'No ready release yet — create one under Releases before provisioning.' : 'Backend + CRM version to install.'}
                  >
                    {(releases.data ?? []).map((r) => (
                      <MenuItem key={r.id} value={r.id} disabled={r.status !== 'ready'}>
                        {r.name} {r.status !== 'ready' && `(${r.status})`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={7} />
                <Grid item xs={12} sm={3}>
                  <TextField select fullWidth label="Licence" value={client.licence} onChange={(e) => setC({ licence: e.target.value as ClientForm['licence'] })}>
                    <MenuItem value="">—</MenuItem>
                    <MenuItem value="rent">Rent</MenuItem>
                    <MenuItem value="sale">Sale</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={7} sm={3}>
                  <TextField fullWidth label="Monthly fee" value={client.monthlyFee} onChange={(e) => setC({ monthlyFee: e.target.value.replace(',', '.') })} inputProps={{ inputMode: 'decimal' }} {...err('monthlyFee')} />
                </Grid>
                <Grid item xs={5} sm={2}>
                  <TextField select fullWidth label="Currency" value={client.feeCurrency} onChange={(e) => setC({ feeCurrency: e.target.value })}>
                    {FEE_CURRENCIES.map((x) => (
                      <MenuItem key={x} value={x}>
                        {x}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="date" label="Renewal date" value={client.renewalDate} onChange={(e) => setC({ renewalDate: e.target.value })} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={2} label="Notes" value={client.notes} onChange={(e) => setC({ notes: e.target.value })} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {step === 1 && (
          <Card>
            <CardContent>
              <TenantForm
                value={tenant}
                onChange={(t) => {
                  setTenantTouched(true);
                  setTenant(t);
                }}
                errors={tErr}
                showErrors={!!showErrors[1]}
              />
            </CardContent>
          </Card>
        )}

        {step === 2 &&
          (envQ.isLoading || draft.isLoading ? (
            <SkeletonRows rows={5} height={64} />
          ) : envQ.isError ? (
            <ErrorState error={envQ.error} onRetry={() => void envQ.refetch()} />
          ) : (
            <Stack spacing={2}>
              <Alert severity={missing.length ? 'warning' : 'success'}>
                {missing.length
                  ? `${missing.length} required ${missing.length === 1 ? 'key' : 'keys'} left. The draft is saved — you can finish them later on the clone's Keys tab; provisioning needs all of them.`
                  : 'All required keys are filled in. Market and brand values default to the tenant answers.'}
              </Alert>
              <EnvForm
                fields={fields}
                edits={env}
                serverMissing={draft.data?.missingRequired}
                groups={[...REQUIRED_GROUPS, ...GROUP_ORDER.filter((g) => !REQUIRED_GROUPS.includes(g))]}
                onEdit={(k, v) =>
                  setEnv((x) => {
                    const n = { ...x };
                    if (v === undefined) delete n[k];
                    else n[k] = v;
                    return n;
                  })
                }
              />
            </Stack>
          ))}

        {step === 3 && (
          <Card>
            <CardContent>
              <FormControlLabel control={<Switch checked={withBrand} onChange={(e) => setWithBrand(e.target.checked)} />} label="Set up the mobile app brand now" />
              <Typography variant="body2" color="text.secondary" sx={{ mb: withBrand ? 2 : 0 }}>
                Optional. The brand (app name, bundle IDs, colours, logo, Firebase files) is only needed to build the rider and staff apps, and can be filled in later on the clone's Mobile apps tab.
              </Typography>
              {withBrand && <BrandForm value={brand} onChange={setBrand} errors={bErr} showErrors={!!showErrors[3]} />}
            </CardContent>
          </Card>
        )}

        {step === 4 && (
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Client
                    </Typography>
                    <KeyValue
                      items={[
                        ['Name', client.name],
                        ['Client', client.clientName],
                        ['Slug', <code key="s">{client.slug}</code>],
                        ['Domain', client.domain],
                        ['Release', release?.name ?? '—'],
                        ['Licence', client.licence ? `${client.licence}${client.monthlyFee ? ` · ${client.monthlyFee} ${client.feeCurrency}/mo` : ''}` : '—'],
                      ]}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Tenant
                    </Typography>
                    <KeyValue
                      items={[
                        ['Brand', tenant.brandName],
                        ['Market', `${tenant.defaultCountry} · ${tenant.currency} · ${tenant.timezone}`],
                        ['City', `${tenant.city.name} (${tenant.city.shortCode})`],
                        ['Ride', `${tenant.prices.unlock ?? 0} unlock + ${tenant.prices.rideMinute ?? 0}/min`],
                        ['Admin', tenant.adminLogin],
                      ]}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            <Card>
              <CardContent>
                <Stack spacing={1}>
                  {(
                    [
                      [stepValid[0], 'Client, slug and domain', 0],
                      [stepValid[1], 'Tenant answers', 1],
                      [missing.length === 0 && !!cloneId, missing.length ? `Keys — ${missing.length} required missing (${missing.slice(0, 4).join(', ')}${missing.length > 4 ? '…' : ''})` : 'Keys', 2],
                      [true, withBrand ? 'Brand' : 'Brand — later', 3],
                    ] as const
                  ).map(([ok, label, i]) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                      {ok ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon color="warning" fontSize="small" />}
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {label}
                      </Typography>
                      {!ok && (
                        <Button size="small" onClick={() => setStep(i)}>
                          Edit
                        </Button>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
            {Object.keys(progress).length > 0 && (
              <Card>
                <CardContent>
                  <Stack spacing={0.75}>
                    {(Object.keys(SUBMIT_LABEL) as SubmitStep[])
                      .filter((s) => progress[s])
                      .map((s) => (
                        <Stack key={s} direction="row" spacing={1} alignItems="center">
                          {progress[s] === 'done' ? <CheckCircleIcon color="success" fontSize="small" /> : progress[s] === 'failed' ? <ErrorOutlineIcon color="error" fontSize="small" /> : <RadioButtonUncheckedIcon color="primary" fontSize="small" />}
                          <Typography variant="body2">{SUBMIT_LABEL[s]}</Typography>
                        </Stack>
                      ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
            {submitError && (
              <Alert
                severity="error"
                action={
                  cloneId && (
                    <Button color="inherit" size="small" component={RouterLink} to={`/clones/${cloneId}`}>
                      Open draft
                    </Button>
                  )
                }
              >
                {submitError}. The draft is saved; fix the problem and try again, or continue on the clone page.
              </Alert>
            )}
            <Alert severity="info" icon={false}>
              Provisioning takes 10–15 minutes. The super admin password is generated and <strong>shown once</strong> when the job finishes.
            </Alert>
          </Stack>
        )}

        {draftError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Could not save the draft: {draftError}
          </Alert>
        )}
        {cloneId && step < 4 && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
            Draft <strong>{client.slug}</strong> is saved — you can leave and finish it from the clone page.
          </Typography>
        )}
        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }} justifyContent="space-between" flexWrap="wrap" useFlexGap>
          <Button onClick={() => (step === 0 ? navigate(-1) : setStep(step - 1))} disabled={submitting}>
            {step === 0 ? 'Cancel' : 'Back'}
          </Button>
          {step < STEPS.length - 1 ? (
            <Stack direction="row" spacing={1} alignItems="center">
              {!stepValid[step] && showErrors[step] && <Chip size="small" color="error" variant="outlined" label="Fix the highlighted fields" />}
              <LoadingButton variant="contained" onClick={() => void next()} loading={savingDraft}>
                {step === 1 ? (cloneId ? 'Save draft and continue' : 'Create draft and continue') : step === 3 && !withBrand ? 'Skip' : 'Next'}
              </LoadingButton>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1}>
              <LoadingButton variant="outlined" loading={submitting} disabled={!cloneId} onClick={() => void submit(false)}>
                Save and finish later
              </LoadingButton>
              <LoadingButton variant="contained" loading={submitting} disabled={!cloneId || !stepValid.every(Boolean) || missing.length > 0} onClick={() => void submit(true)}>
                Create and provision
              </LoadingButton>
            </Stack>
          )}
        </Stack>
      </Box>
    </>
  );
}

